// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { Database, ExternalLink } from 'lucide-react'
import { usePmsName } from '../../auth/AuthContext'

/**
 * Banner displayed on pharmacy-side views to make it explicit that the
 * pharmacy's DISPENSING SYSTEM is the system of record per ADR-014. OwlRx Portal
 * is a read-only mirror + collaboration overlay; staff perform clinical actions
 * there, not here.
 *
 * D-PMS-1: the vendor name is read from the tenant (usePmsName) rather than
 * hardcoded. This banner asserted "PioneerRx" to every pharmacy, including ones
 * running something else entirely — wrong on its face, and the single most
 * visible place that assumption surfaced.
 */
export default function PioneerRxSourceBanner({
  variant = 'queue',
  pioneerRxUrl,
}: {
  variant?: 'queue' | 'detail'
  pioneerRxUrl?: string
}) {
  const { name: pms, known } = usePmsName()
  // With a known vendor the sentence names it; without one it stays neutral
  // instead of inventing a system the pharmacy may not run.
  const heading = known ? `${pms} is the source of truth` : 'Your dispensing system is the source of truth'
  return (
    <div role="status" style={style}>
      <Database size={14} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#0c4a6e' }}>
          {heading}
        </div>
        <div style={{ fontSize: 12, color: '#075985', lineHeight: 1.45, marginTop: 2 }}>
          {variant === 'queue'
            ? `Prescriptions appear here as a read-only mirror of ${pms}. Verification, filling, dispensing, and refill fulfillment all happen in ${pms}; this view updates automatically as actions are taken there.`
            : `This Rx is a read-only mirror of the record in ${pms}. State changes (verify, fill, dispense, hold, etc.) are made in ${pms} and reflected here. The portal is for visibility, comments, and notifications.`}
        </div>
      </div>
      {pioneerRxUrl && (
        <a href={pioneerRxUrl} target="_blank" rel="noreferrer" style={linkStyle}>
          Open in {pms}
          <ExternalLink size={11} aria-hidden="true" />
        </a>
      )}
    </div>
  )
}

const style: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
  background: '#f0f9ff',
  border: '1px solid #bae6fd',
  color: '#0c4a6e',
  padding: '10px 14px',
  borderRadius: 6,
  marginBottom: 16,
  fontFamily: 'inherit',
}

const linkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 12,
  fontWeight: 500,
  color: '#0369a1',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,
}
